import { Navigate, useRoutes } from "react-router-dom";
import DashboardLayout from "../layouts/dashboard";
import {
  GeneralAppPage,
  DiaryPage,
  PatientsListPage,
  LoginPage,
  ResetPasswordPage,
  NewPasswordPage,
  Page500,
  Page404,
  Page403,
  SettingsPage,
  RegisterPage,
  SignupPage,
  DispensaryListPage,
  NewRepairPage,
  RepairListPage,
} from "./elements";
import AuthGuard from "../auth/AuthGuard";
import RoleGuard from "../auth/RoleGuard";
import GuestGuard from "../auth/GuestGuard";
import AppLayout from "../layouts/app";
import CompactLayout from "../layouts/compact";
import PatientClinicalRecords from "../pages/dashboard/Patient/PatientClinicalRecords";
import TillPage from "../pages/dashboard/Till/TillPage";
import TillListPage from "../pages/dashboard/Till/TillListPage";
import RecallsPage from "../pages/dashboard/Recalls/RecallsPage";
import ReportingPage from "../pages/dashboard/Reporting/ReportingPage";
import DirectDebitsPage from "../pages/dashboard/DirectDebits/DirectDebitsPage";
import RegisterGuard from "../auth/RegisterGuard";
import PointOfSaleListPage from "../pages/dashboard/PointOfSale/PointOfSaleListPage";
import PointOfSalePage from "../pages/dashboard/PointOfSale/PointOfSalePage";
import PointOfSaleDetailsPage from "../pages/dashboard/PointOfSale/PointOfSaleDetailsPage";
import PatientRouteWrapper from "../components/PatientRouteWrapper";
import VideoLandingPage from "../pages/landing";
import useDomain from "../hooks/useDomain";
import DispensePage from "../pages/dashboard/Dispense/DispensePage";
import ProductListPage from "../pages/dashboard/Product/ProductListPage";
import ProductPage from "../pages/dashboard/Product/ProductPage";
import ProductDetailsPage from "../pages/dashboard/Product/ProductDetailsPage";
import PaymentSuccessPage from "../pages/dashboard/Till/PaymentSuccess";
import PaymentCancelledPage from "../pages/dashboard/Till/PaymentCancelled";
import GoCardlessCallback from "../pages/dashboard/go-cardless/GoCardlessCallback";

export default function Router() {
  const { isPrimary } = useDomain();
  return useRoutes([
    // ---------- AUTH ROUTES ----------
    {
      element: <CompactLayout />,
      path: "auth",
      children: [
        {
          path: "login",
          element: (
            <GuestGuard>
              <LoginPage />
            </GuestGuard>
          ),
        },
        ...(isPrimary
          ? [
              {
                path: "demo-register",
                element: (
                  <GuestGuard>
                    <RegisterPage />
                  </GuestGuard>
                ),
              },
              {
                path: "signup",
                element: (
                  <GuestGuard>
                    <SignupPage />
                  </GuestGuard>
                ),
              },
              {
                children: [
                  { path: "reset-password", element: <ResetPasswordPage /> },
                  { path: "new-password", element: <NewPasswordPage /> },
                ],
              },
            ]
          : []),
      ],
    },

    // ---------- ROOT / HOME ----------
    {
      path: "/",
      element: isPrimary ? (
        <VideoLandingPage />
      ) : (
        <Navigate to="/auth/login" replace />
      ),
    },

    // Payment Routes (outside of AuthGuard for easier access from external redirects)
    {
      path: "/payment-success",
      element: (
        <AuthGuard>
          <PaymentSuccessPage />
        </AuthGuard>
      ),
    },
    {
      path: "/payment-failed",
      element: (
        <AuthGuard>
          <PaymentCancelledPage />
        </AuthGuard>
      ),
    },
    {
      path: "/",
      element: (
        <AuthGuard>
          <AppLayout />
        </AuthGuard>
      ),
      children: [
        {
          path: "",
          element: (
            <RoleGuard>
              <DashboardLayout />
            </RoleGuard>
          ),
          children: [
            { path: "", element: <GeneralAppPage /> },
            { path: "diary", element: <DiaryPage /> },
            {
              path: "patients",
              children: [
                { path: "list", element: <PatientsListPage /> },
                { path: ":patientId", element: <PatientClinicalRecords /> },
              ],
            },
            {
              path: "dispense",
              children: [
                {
                  path: "list",
                  element: (
                    <PatientRouteWrapper moduleType="dispense">
                      <DispensaryListPage />
                    </PatientRouteWrapper>
                  ),
                },
                {
                  path: "new",
                  element: <DispensePage />,
                },
                {
                  path: "repair",
                  children: [
                    { path: "", element: <NewRepairPage /> },
                    { path: "list", element: <RepairListPage /> },
                  ],
                },
                {
                  path: ":dispenseId",
                  element: <DispensePage />,
                },
              ],
            },
            {
              path: "till",
              children: [
                {
                  path: "list",
                  element: (
                    <PatientRouteWrapper moduleType="till">
                      <TillListPage />
                    </PatientRouteWrapper>
                  ),
                },
                {
                  path: "new",
                  element: <h1>New Sale...</h1>,
                },
                {
                  path: ":tillId",
                  element: <TillPage />,
                },
                {
                  path: "external",
                  element: <h1>External Sale...</h1>,
                },
                {
                  path: "cash",
                  element: <h1>Coming soon...</h1>,
                },
              ],
            },
            {
              path: "recalls",
              children: [{ path: "list", element: <RecallsPage /> }],
            },
            {
              path: "reporting",
              children: [{ path: "list", element: <ReportingPage /> }],
            },
            {
              path: "debits",
              children: [
                { path: "list", element: <DirectDebitsPage /> },
                { path: "callback", element: <GoCardlessCallback /> },
              ],
            },
            {
              path: "product",
              children: [
                {
                  path: "list",
                  element: <ProductListPage />,
                },
                {
                  path: ":productId",
                  element: <ProductPage />,
                },
                {
                  path: ":productId/details",
                  element: <ProductDetailsPage />,
                },
                {
                  path: "new",
                  element: <ProductPage />,
                },
              ],
            },
            // {
            //   path: "pos",
            //   children: [
            //     {
            //       path: "list",
            //       element: <PointOfSaleListPage />,
            //     },
            //     {
            //       path: ":PointOfSaleId",
            //       element: <PointOfSalePage />,
            //     },
            //     {
            //       path: ":PointOfSaleId/details",
            //       element: <PointOfSaleDetailsPage />,
            //     },
            //     {
            //       path: "new",
            //       element: <PointOfSalePage />,
            //     },
            //   ],
            // },
            // {
            //   path: "user",
            //   children: [
            //     {
            //       path: "list",
            //       element: <UserAccountPage />,
            //     },
            //   ],
            // },
            {
              path: "settings",
              children: [
                { path: "", element: <SettingsPage /> },
                { path: "users", element: <SettingsPage route={"user"} /> },
                {
                  path: "appointments",
                  element: <SettingsPage route={"appointment"} />,
                },
                { path: "rota", element: <SettingsPage route={"rota"} /> },
                {
                  path: "inventory-settings",
                  element: <SettingsPage route={"inventorySettings"} />,
                },
                {
                  path: "inventory-control",
                  element: <SettingsPage route={"inventoryControl"} />,
                },
                {
                  path: "procurement",
                  element: <SettingsPage route={"procurement"} />,
                },
                { path: "parts", element: <SettingsPage route={"parts"} /> },
              ],
            },
          ],
        },
      ],
    },

    // ---------- ERROR PAGES ----------
    {
      element: <CompactLayout />,
      children: [
        { path: "500", element: <Page500 /> },
        { path: "404", element: <Page404 /> },
        { path: "403", element: <Page403 /> },
      ],
    },
    { path: "*", element: <Navigate to="/404" replace /> },
  ]);
}
